// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CartLinesListStyles } from "@rpsc/Overrides/Components/CartLinesList";
import CartLinesListHeader, { CartLinesListHeaderStyles } from "@rpsc/Overrides/Components/CartLinesListHeader";
import { CartLinesDefinitions } from "@rpsc/Overrides/Widgets/Cart/CartLines";
import React, { FC, useMemo, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: ApplicationState) => {
    const { cartId } = state.pages.onePageCheckout;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);

    return {
        cartState,
    };
};

const mapDispatchToProps = {};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CheckoutReviewAndSubmitProductListStyles {
    orderLinesList?: CartLinesListStyles;
    cartLinesListHeader?: CartLinesListHeaderStyles;
}

export const productListStyles: CheckoutReviewAndSubmitProductListStyles = {};

const styles = productListStyles;

const OnePageCheckoutProductList: FC<Props> = ({ cartState, fields }) => {
    const [isCondensed, setIsCondensed] = useState(false);

    // XNG-Change: RPSC-866 - hide quoted items from checkout page only
    const externalCart = useMemo(() => {
        return {
            ...cartState?.value,
            cartLines: cartState?.value?.cartLines?.filter(cartLine => !cartLine.quoteRequired),
        };
    }, [cartState?.value]);

    return (
        <>
            {externalCart && (
                <CartLinesListHeader
                    isCondensed={isCondensed}
                    productsCount={externalCart.cartLines?.length || 0}
                    showEditCartLink
                    onChangeIsCondensed={() => setIsCondensed(condensed => !condensed)}
                    extendedStyles={styles.cartLinesListHeader}
                />
            )}
            {createWidgetElement("Cart/CartLines", {
                fields,
                editable: false,
                showEditCartLink: true,
                forceCondensed: isCondensed,
                externalCart
            } as any)}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OnePageCheckoutProductList),
    definition: {
        displayName: "Product List",
        group: "One Page Checkout",
        allowedContexts: ["OnePageCheckoutPage"],
        fieldDefinitions: [...CartLinesDefinitions],
    },
};

export default widgetModule;
